import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, TaskItem, TaskDetail, TaskStats, TaskReminder } from "@/lib/types"

interface TaskFilters {
  status?: string
  priority?: string
  assignee_id?: string
  due_date_from?: string
  due_date_to?: string
  entity_type?: string
  entity_id?: string
  search?: string
  parent_task_id?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: TaskFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useTasks(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: ["tasks", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<TaskItem>>(`/api/v1/tasks${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useTask(id?: string) {
  return useQuery({
    queryKey: ["tasks", id],
    queryFn: () => api.get<{ data: TaskDetail }>(`/api/v1/tasks/${id}`),
    enabled: !!id,
  })
}

export function useTaskStats() {
  return useQuery({
    queryKey: ["tasks", "stats"],
    queryFn: () => api.get<{ data: TaskStats }>("/api/v1/tasks/stats"),
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: TaskDetail }>("/api/v1/tasks", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useUpdateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: TaskDetail }>(`/api/v1/tasks/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: TaskDetail }>(`/api/v1/tasks/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useReopenTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: TaskDetail }>(`/api/v1/tasks/${id}/reopen`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useAddTaskLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, entity_type, entity_id }: { taskId: string; entity_type: string; entity_id: string }) =>
      api.post(`/api/v1/tasks/${taskId}/links`, { entity_type, entity_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useRemoveTaskLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, linkId }: { taskId: string; linkId: string }) =>
      api.delete(`/api/v1/tasks/${taskId}/links/${linkId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useAddChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) =>
      api.post(`/api/v1/tasks/${taskId}/checklist`, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useUpdateChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, itemId, data }: { taskId: string; itemId: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/tasks/${taskId}/checklist/${itemId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useRemoveChecklistItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, itemId }: { taskId: string; itemId: string }) =>
      api.delete(`/api/v1/tasks/${taskId}/checklist/${itemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

// Reminders
export function useAddReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: { remind_at: string; offset_type: string; delivery_method: string } }) =>
      api.post<{ data: TaskReminder }>(`/api/v1/tasks/${taskId}/reminders`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useUpdateReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, reminderId, data }: { taskId: string; reminderId: string; data: Record<string, unknown> }) =>
      api.patch<{ data: TaskReminder }>(`/api/v1/tasks/${taskId}/reminders/${reminderId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}

export function useRemoveReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, reminderId }: { taskId: string; reminderId: string }) =>
      api.delete(`/api/v1/tasks/${taskId}/reminders/${reminderId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
    },
  })
}
